import { useElplanInfo, useElplanInfoTagsForDate } from 'api/elplan/elplan.api'
import type { ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import elplanStore from 'store/elplan/elplan'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { resolveBidInputCurrency } from './bidInputCurrency.helper'

type DayAheadBidTagSelectionContext = {
  tagsForDate: ElplanInfoTagForDate[]
  tagsStatus: 'pending' | 'error' | 'ready'
  requireTagForBlockBids: boolean
  blockBidInputCurrency: string
  profileBidInputCurrency: string
}

type UseDayAheadBidTagSelectionContextParams = {
  partitionId?: number
}

/**
 * Resolves the tag-selection and currency context that both the day-ahead
 * management tab and the scenario-planning tab need when editing block and
 * profile bids. Reads the selected date and elplan info id from the shared
 * elplan store so callers do not have to recompute them.
 */
export function useDayAheadBidTagSelectionContext({
  partitionId,
}: UseDayAheadBidTagSelectionContextParams = {}): DayAheadBidTagSelectionContext {
  const { systemId, activeSystem } = useAuth()
  const elplanSnap = useSnapshot(elplanStore)
  const { data: elplanInfo } = useElplanInfo(systemId)

  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const bidDate = Datetime.toLocalTime(selectedStartTime, 'onlyDate') as ISODateTime
  const requireTagForBlockBids = elplanInfo?.require_tag_for_block_bids ?? false

  const {
    data: tagsForDate = [],
    isPending,
    isError,
  } = useElplanInfoTagsForDate(elplanSnap.elplan_info_id, bidDate, partitionId, requireTagForBlockBids)

  // `ready` means "nothing further to wait for", which covers both a loaded tag list and a system
  // with no tag concept at all: the query is disabled where tags are not required, and a disabled
  // query reports `pending` forever in TanStack v5. The two are not distinguished because the only
  // surfaces that branch on this are already gated on `requireTagForBlockBids`.
  const tagsStatus = !requireTagForBlockBids ? 'ready' : isError ? 'error' : isPending ? 'pending' : 'ready'

  const blockBidInputCurrency = resolveBidInputCurrency(elplanInfo?.bid_types, 'blockbid', activeSystem?.currency)
  const profileBidInputCurrency = resolveBidInputCurrency(elplanInfo?.bid_types, 'profilebid', activeSystem?.currency)

  return {
    tagsForDate,
    tagsStatus,
    requireTagForBlockBids,
    blockBidInputCurrency,
    profileBidInputCurrency,
  }
}
